import {
  usePageConfig,
  useTabContentConfig,
} from '../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../components/PageConfigProvider/types';
import { useFilterContext } from '../contexts/FilterProvider';
import {
  type ConsistencyKey,
  generatePageKey,
  NO_PRIMARY_FILTER_KEY,
} from '../engine/filterGroupConsistency';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
  type PrimarySelection,
  type TreeFilter,
} from '../types/filters';

type PrimaryFilterStateResult = {
  primaryFilter: TreeFilter | null;
  activePrimaryFilterLength: number;
  togglePrimaryFilter: (selectionId: string) => void;
  removePrimaryFilter: (selectionId: string) => void;
  reorderPrimarySelections: (newPrimarySelections: PrimarySelection[]) => void;
};

const noop = () => undefined;

const isPrimaryFilterId = (key: ConsistencyKey): key is PrimaryFilterId => {
  return (
    key !== NO_PRIMARY_FILTER_KEY &&
    key !== RevelioPageId.COMPANY_SCREENER_V2 &&
    key !== RevelioPageId.GEOGRAPHY_SCREENER_V2 &&
    key !== RevelioPageId.ROLE_SCREENER_V2 &&
    (key === FilterId.COMPANY ||
      key === FilterId.GEOGRAPHY ||
      key === FilterId.ROLE)
  );
};

export const usePrimaryFilterState = (
  filterId?: PrimaryFilterId
): PrimaryFilterStateResult => {
  const { dispatch, filterState, activeFilterMap } = useFilterContext();
  const { primaryFilter: tabContentPrimaryFilter, revelioPageId } =
    useTabContentConfig();
  const { config: pageConfig } = usePageConfig();

  if (!filterId || !tabContentPrimaryFilter) {
    return {
      primaryFilter: null,
      activePrimaryFilterLength: 0,
      togglePrimaryFilter: noop,
      removePrimaryFilter: noop,
      reorderPrimarySelections: noop,
    };
  }

  const limit = tabContentPrimaryFilter.selectionLimit ?? 5;
  const activeKey =
    filterState.activeKey ??
    tabContentPrimaryFilter.id ??
    NO_PRIMARY_FILTER_KEY;
  const pageKey = generatePageKey(activeKey, revelioPageId ?? pageConfig.id);
  const primaryFilter = (() => {
    if (!isPrimaryFilterId(activeKey)) {
      return null;
    }
    const filter = activeFilterMap.get(activeKey);
    if (filter?.type === FilterType.TREE && filter.isPrimary) {
      return filter;
    }
    return null;
  })();

  const rawPagePrimarySelections =
    filterState.pageMap.get(pageKey)?.get(filterId) ||
    primaryFilter?.primarySelections ||
    [];

  // `filterState.pageMap` is the reducer's internal primary-selection state.
  // It carries the `isActive` / ordering metadata but not the taxonomy-patched
  // display name. `primaryFilter.primarySelections` (from `activeFilterMap`)
  // DOES carry the patched name (see `applyResolvedSelectionLabelsToFilterMap`).
  // Merge the patched name across by id so ROLE/SKILL primary chips show the
  // correct label after a cross-scope AI apply, without losing the pageMap's
  // isActive/ordering metadata.
  const pagePrimarySelections = (() => {
    const patchedPrimary = primaryFilter?.primarySelections;
    if (!patchedPrimary || patchedPrimary.length === 0) {
      return rawPagePrimarySelections;
    }
    const patchedNameById = new Map(
      patchedPrimary.map((p) => [p.id, p.name] as const)
    );
    let changed = false;
    const merged = rawPagePrimarySelections.map((p) => {
      const patchedName = patchedNameById.get(p.id);
      if (patchedName !== undefined && patchedName !== p.name) {
        changed = true;
        return { ...p, name: patchedName };
      }
      return p;
    });
    return changed ? merged : rawPagePrimarySelections;
  })();

  const togglePrimaryFilter = (selectionId: string) => {
    dispatch({
      type: 'TOGGLE_PRIMARY_SELECTION_ACTIVE',
      payload: { filterId, selectionId, pageKey, limit },
    });
  };

  const removePrimaryFilter = (selectionId: string) => {
    dispatch({
      type: 'REMOVE_PRIMARY_SELECTION',
      payload: { filterId, selectionId, pageKey },
    });
  };

  const reorderPrimarySelections = (
    newPrimarySelections: PrimarySelection[]
  ) => {
    dispatch({
      type: 'REORDER_PRIMARY_SELECTIONS',
      payload: { filterId, newPrimarySelections, pageKey },
    });
  };

  // Use the geography-adjusted primarySelections from activeFilterMap when
  // available, as the pageMap cache may still hold stale MSA/state selections
  // from before a geography mode switch.
  const adjustedPrimarySelections =
    filterId === FilterId.GEOGRAPHY && primaryFilter?.primarySelections?.length
      ? primaryFilter.primarySelections
      : pagePrimarySelections;

  const mergedPrimaryFilter = primaryFilter
    ? { ...primaryFilter, primarySelections: adjustedPrimarySelections }
    : null;

  const activePrimaryFilterLength =
    mergedPrimaryFilter?.primarySelections?.filter(
      (selection) => selection?.isActive
    ).length || 0;

  return {
    primaryFilter: mergedPrimaryFilter,
    activePrimaryFilterLength,
    togglePrimaryFilter,
    removePrimaryFilter,
    reorderPrimarySelections,
  };
};
